// @flow

// eslint-disable-next-line import/no-unresolved -- eslint-plugin-import has trouble finding ES modules
import Cookies from 'js-cookie';
import { retrieveRemarketingProperties } from './analytics/remarketing';
import {
  object,
  record,
  nullable,
  string,
  withDefault
} from 'common/json/extract';
import { retrieve } from 'putki';
import { currentSite } from 'site/currentSite';
import { trackClicksOn } from 'utils/analytics';
import { consentGivenForCategory } from 'utils/cookieConsent';
import { generateUserId } from 'utils/trackingUserId';

function loadAnalytics() {
  // Now it's time to inject Jitsu on the page if we can
  const jitsuDomain = retrieve('jitsu_domain');
  const jitsuBrowserWriteKey = retrieve('jitsu_browser_write_key');
  if (
    typeof jitsuDomain !== 'string' ||
    typeof jitsuBrowserWriteKey !== 'string'
  ) {
    return;
  }

  // Inject the Jitsu script tag to the page, triggering the loading of it once done.
  const jitsuScriptTag = document.createElement('script');
  jitsuScriptTag.async = true;
  jitsuScriptTag.src = `https://${jitsuDomain}/p.js`;

  // Configure Jitsu with data tags as that works the best when loading the JS
  // code asynchronously. For example, we can't stop Jitsu from automatically
  // sending a `page` tracking event otherwise.
  // Full configuration reference: https://docs.jitsu.com/sending-data/js-reference
  jitsuScriptTag.setAttribute('data-write-key', jitsuBrowserWriteKey);
  jitsuScriptTag.setAttribute('data-init-only', 'true');
  jitsuScriptTag.setAttribute(
    'data-cookie-names',
    JSON.stringify({
      // A returning visitor's identity lives in these two cookies. Renaming
      // either one makes Jitsu treat every existing visitor as new.
      anonymousId: 'ajs_anonymous_id',
      userId: 'ajs_user_id'
    })
  );
  // eslint-disable-next-line no-restricted-globals -- global location usage is expected
  if (location.search.includes('debugJitsu=1')) {
    jitsuScriptTag.setAttribute('data-debug', 'true');
  }
  // Once Jitsu has loaded, we'll set the anonymous ID as the very first thing so all
  // the queued events will already have the correct anonymous ID set.
  window.jitsuOnLoadCallback = jitsu => {
    const serverSideAnonymousId = Cookies.get('cheekify_a');
    if (serverSideAnonymousId) {
      jitsu.setAnonymousId(Cookies.get('cheekify_a'));
    }
  };
  jitsuScriptTag.setAttribute('data-onload', 'jitsuOnLoadCallback');

  const firstScriptTag = document.getElementsByTagName('script')[0];
  firstScriptTag.parentNode?.insertBefore(jitsuScriptTag, firstScriptTag);
}

function loadAnalyticsOnConsent() {
  document.addEventListener('cookieyes_consent_update', eventData => {
    // $FlowIgnore[prop-missing] -- https://www.cookieyes.com/documentation/events-on-cookie-banner-interactions/
    const data = eventData.detail;
    if (data.accepted.includes('analytics')) {
      loadAnalytics();
    }
  });
}

/**
 * Rebinds tracking of anchor elements with data-venuu-t attributes.
 */
function rebindTrackedLinks() {
  trackClicksOn(
    Array.from(
      document.querySelectorAll('a[data-venuu-t]:not([data-venuu-t-react])')
    )
  );
}

const extractUserTrackingData = withDefault(
  object(
    record({
      email: nullable(string),
      customer_type: nullable(string)
    })
  ),
  { email: null, customer_type: null }
);

/**
 * The current URL's query parameters. `URLSearchParams` substitutes U+FFFD for a
 * malformed percent escape instead of throwing, so a mangled tracking link
 * costs at most the one parameter it mangled.
 */
function queryParams(): URLSearchParams {
  return new URL(window.location.href).searchParams;
}

/**
 * Persists the gclid parameter from the url or the _gcl_aw-cookie to our own cookie.
 *
 * This is done to ensure that we are not fully dependent on the poorly documented
 * Google conversion linker and the corresponding _gcl_aw cookie.
 */
function persistGclid() {
  const gclidFromUrl: ?string = queryParams().get('gclid');

  const gclidCookie: ?string = Cookies.get('_gcl_aw');
  const gclidFromCookie: ?string = gclidCookie && gclidCookie.split('.')[2];

  const gclid = gclidFromUrl || gclidFromCookie;

  if (gclid) {
    Cookies.set('venuu_gclid', gclid, { expires: 90, path: '/' });
  }
}

function distinctIdFromUrl() {
  return queryParams().get('distinct_id');
}

function emailIdFromUrl() {
  const rawEmailDistinctId: string | null =
    queryParams().get('email_distinct_id');
  if (!rawEmailDistinctId) {
    return null;
  }
  return generateUserId({ userEmail: rawEmailDistinctId });
}

/**
 * The following functions are a part of the improved user tracking scheme.
 * ------------------------------------------------
 * Documentation: https://github.com/venuu/venuu/pull/7368
 */
function identifyUser() {
  const userIdFromUrl = distinctIdFromUrl();
  const emailUserIdFromUrl = emailIdFromUrl();
  const userIdFromCookie = Cookies.get('cheekify_uid');
  const { email, customer_type: customerType } = extractUserTrackingData(
    retrieve('user_tracking_data')
  );

  const commonTraits = {
    'Application Version': retrieve('application_version'),
    'Site Key': currentSite().key
  };

  const identify = (...args: any) => {
    (window.jitsuQ = window.jitsuQ || []).push(jitsu => {
      jitsu.identify(...args);
    });
  };

  if (userIdFromCookie && email) {
    const traits = {
      ...commonTraits,
      Source: 'Venuu web app',
      'Customer Type': customerType,
      email: email
    };
    if (traits['Customer Type'] === null) {
      // $FlowFixMe[incompatible-type] -- Suppressed during Flow upgrade to 0.201.0. Delete this comment to view the error.
      delete traits['Customer Type'];
    }
    identify(userIdFromCookie, traits);
  } else if (userIdFromUrl) {
    // These are returning users
    // so they should customer identification traits set.
    identify(userIdFromUrl, commonTraits);
  } else if (emailUserIdFromUrl) {
    identify(emailUserIdFromUrl, commonTraits);
  }
}

export function initializeAnalytics() {
  if (consentGivenForCategory('analytics')) {
    loadAnalytics();
  } else {
    loadAnalyticsOnConsent();
  }

  identifyUser();

  const pageOpts = retrieveRemarketingProperties();

  (window.jitsuQ = window.jitsuQ || []).push(jitsu => {
    jitsu.page(pageOpts);
  });

  // Track special links
  rebindTrackedLinks();

  persistGclid();
}
